"use client";

import TFLink from "@/Web/Components/TFLink/TFLink";
import { ReactNode } from "react";
import { useDirection } from "@/utils/useDirection";

export interface StatisticItem {
  title: string;
  value: string | number;
}

export interface SideInfo {
  title?: string;
  description: string;
  statistics?: StatisticItem[];
  position?: "left" | "right";
}

interface TFLayoutHomeSectionProps {
  title?: string;
  description?: string;
  moreButton?: {
    title: string;
    href: string;
  };
  children: ReactNode;
  className?: string;
  sideInfo?: SideInfo;
}

interface SectionHeaderProps {
  title: string;
  description?: string;
}

const SectionHeader = ({ title, description }: SectionHeaderProps) => {
  return (
    <div className="mb-6 text-center">
      <h2 className="text-4xl font-light leading-normal">{title}</h2>
      {description && (
        <p className="text-xl max-w-2xl mx-auto mt-2">{description}</p>
      )}
    </div>
  );
};

interface StatisticsProps {
  statistics: StatisticItem[];
}

const Statistics = ({ statistics }: StatisticsProps) => {
  if (statistics.length === 0) return null;

  return (
    <div className="flex gap-4 justify-center">
      {statistics.map((stat, index) => (
        <div key={index} className="flex-1 text-center">
          <div className="text-xs uppercase tracking-wider font-bold mb-2">
            {stat.title}
          </div>
          <div className="text-2xl font-light text-black tabular-nums">
            {stat.value}
          </div>
        </div>
      ))}
    </div>
  );
};

interface SidePanelContentProps {
  title?: string;
  description: string;
  statistics?: StatisticItem[];
  moreButton?: {
    title: string;
    href: string;
  };
}

const SidePanelContent = ({
  title,
  description,
  statistics,
  moreButton,
}: SidePanelContentProps) => {
  const direction = useDirection();
  const isRTL = direction === "rtl";
  
  return (
    <div className="space-y-6 text-center">
      {title && <h3 className="text-4xl font-light leading-normal">{title}</h3>}

      <p className="text-lg font-light leading-relaxed">{description}</p>

      {statistics && <Statistics statistics={statistics} />}

      {moreButton && (
        <TFLink
          href={moreButton.href}
          className="px-8 py-4 bg-black/5 rounded-full font-medium hover:bg-black/10 transition-colors inline-block"
        >
          {moreButton.title} {isRTL ? "←" : "→"}
        </TFLink>
      )}
    </div>
  );
};

interface SidePanelProps {
  sideInfo: SideInfo;
  moreButton?: {
    title: string;
    href: string;
  };
}

const SidePanel = ({ sideInfo, moreButton }: SidePanelProps) => {
  return (
    <div className="self-center order-0 lg:order-none">
      <SidePanelContent
        title={sideInfo.title}
        description={sideInfo.description}
        statistics={sideInfo.statistics}
        moreButton={moreButton}
      />
    </div>
  );
};

interface MoreButtonProps {
  title: string;
  href: string;
}

const MoreButton = ({ title, href }: MoreButtonProps) => {
  const direction = useDirection();
  const isRTL = direction === "rtl";
  
  return (
    <div className="text-center mt-6">
      <TFLink
        href={href}
        className="px-8 py-4 bg-black/5 rounded-full font-medium hover:bg-black/10 transition-colors inline-block"
      >
        {title} {isRTL ? "←" : "→"}
      </TFLink>
    </div>
  );
};

const TFLayoutHomeSection = ({
  title,
  description,
  moreButton,
  children,
  className = "",
  sideInfo,
}: TFLayoutHomeSectionProps) => {
  const direction = useDirection();
  const isRTL = direction === "rtl";
  const hasSideInfo = !!sideInfo;
  const sidePosition = sideInfo?.position || "right";
  
  // In RTL, reverse the side position logic
  const effectiveSidePosition = isRTL 
    ? (sidePosition === "left" ? "right" : "left")
    : sidePosition;

  return (
    <section className={`py-4 px-4 ${className}`}>
      <div className="max-w-7xl mx-auto">
        {title && <SectionHeader title={title} description={description} />}

        {hasSideInfo ? (
          <div
            className={`grid gap-12 ${
              effectiveSidePosition === "left"
                ? "grid-cols-1 lg:grid-cols-[320px_1fr]"
                : "grid-cols-1 lg:grid-cols-[1fr_320px]"
            }`}
          >
            {effectiveSidePosition === "left" && (
              <SidePanel sideInfo={sideInfo} moreButton={moreButton} />
            )}

            <div className="min-w-0 order-1 lg:order-none">{children}</div>

            {effectiveSidePosition === "right" && (
              <SidePanel sideInfo={sideInfo} moreButton={moreButton} />
            )}
          </div>
        ) : (
          <div className="">
            {children}
            {moreButton && (
              <MoreButton title={moreButton.title} href={moreButton.href} />
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default TFLayoutHomeSection;
